import React, { useState, useEffect, useRef, useCallback } from "react";
import { View, TouchableOpacity, Text, StyleSheet, Dimensions, Animated, Modal, Pressable, ScrollView } from "react-native";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

import { app } from "../../firebase";


const { width } = Dimensions.get('window');
const isMobile = width < 768;

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Normalize any image entry into { uri?, _requireModule?, position }.
 * Stored images may be plain strings (legacy), { uri, position } objects,
 * or require() asset modules (preview mode).
 */
const normalizeImage = (img) => {
	if (!img) return { uri: '', position: '50% 50%' };
	if (typeof img === 'string') return { uri: img, position: '50% 50%' };
	if (img.uri) return { uri: img.uri, position: img.position || '50% 50%' };
	// require() asset module (number or RN asset object)
	return { _requireModule: img, position: '50% 50%' };
};

// ── Reposition Modal ───────────────────────────────────────────────────────────

const RepositionModal = ({ image, onSave, onCancel }) => {
	const normalized = normalizeImage(image);

	const parsePos = (pos) => {
		const parts = (pos || '50% 50%').split(' ');
		return { x: parseFloat(parts[0]) || 50, y: parseFloat(parts[1]) || 50 };
	};

	const initial = parsePos(normalized.position);
	const [posX, setPosX] = useState(initial.x);
	const [posY, setPosY] = useState(initial.y);
	const isDragging = useRef(false);
	const lastPos = useRef({ x: 0, y: 0 });

	// Register mouse handlers on window so drag keeps working even if cursor
	// leaves the image preview area
	useEffect(() => {
		const onMove = (e) => {
			if (!isDragging.current) return;
			const dx = e.clientX - lastPos.current.x;
			const dy = e.clientY - lastPos.current.y;
			lastPos.current = { x: e.clientX, y: e.clientY };
			// Drag direction = moving the photo: right → posX decreases (see more of left)
			setPosX((prev) => Math.max(0, Math.min(100, prev - dx * 0.3)));
			setPosY((prev) => Math.max(0, Math.min(100, prev - dy * 0.3)));
		};
		const onUp = () => { isDragging.current = false; };
		window.addEventListener('mousemove', onMove);
		window.addEventListener('mouseup', onUp);
		return () => {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
		};
	}, []);

	// Can't reposition local require() assets — no src URL available
	if (normalized._requireModule) return null;

	// Use RN Modal so it renders above ALL stacking contexts / overflow:hidden parents.
	// The inner drag preview stays as HTML so objectPosition and onMouseDown work.
	return (
		<Modal visible transparent animationType="fade" onRequestClose={onCancel}>
			<View style={repoStyles.overlay}>
				<Pressable style={repoStyles.backdropHit} onPress={onCancel} />
				<View style={repoStyles.card}>
					<Text style={repoStyles.title}>Reposition Photo</Text>
					<Text style={repoStyles.subtitle}>
						Drag the image to choose what part is visible in the slideshow.
					</Text>

					{/* Drag-to-reposition preview — plain HTML so objectPosition and
					    mouseDown events work natively without RN Web interception */}
					<div
						onMouseDown={(e) => {
							isDragging.current = true;
							lastPos.current = { x: e.clientX, y: e.clientY };
							e.preventDefault();
						}}
						style={{
							width: '100%',
							aspectRatio: '16/10',
							borderRadius: 12,
							overflow: 'hidden',
							cursor: 'grab',
							userSelect: 'none',
							backgroundColor: '#111',
							position: 'relative',
							marginBottom: 6,
						}}
					>
						<img
							src={normalized.uri}
							draggable={false}
							alt="reposition preview"
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'cover',
								objectPosition: `${posX}% ${posY}%`,
								pointerEvents: 'none',
								display: 'block',
							}}
						/>
						{/* Rule-of-thirds grid */}
						<div style={{
							position: 'absolute', inset: 0,
							backgroundImage: 'linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)',
							backgroundSize: '33.33% 33.33%',
							pointerEvents: 'none',
						}} />
						{/* Crosshair marker at focal point */}
						<div style={{
							position: 'absolute',
							left: `${posX}%`, top: `${posY}%`,
							transform: 'translate(-50%, -50%)',
							pointerEvents: 'none',
							width: 24, height: 24,
							borderRadius: 12,
							border: '2.5px solid #fff',
							boxShadow: '0 0 0 1.5px rgba(0,0,0,0.5)',
						}} />
					</div>

					<Text style={repoStyles.coords}>
						{Math.round(posX)}% from left · {Math.round(posY)}% from top
					</Text>

					<View style={repoStyles.btnRow}>
						<TouchableOpacity
							style={[repoStyles.btn, repoStyles.applyBtn]}
							onPress={() => onSave({ position: `${posX.toFixed(1)}% ${posY.toFixed(1)}%` })}
							activeOpacity={0.8}
						>
							<Text style={repoStyles.applyBtnText}>Apply</Text>
						</TouchableOpacity>
						<TouchableOpacity
							style={[repoStyles.btn, repoStyles.cancelBtn]}
							onPress={onCancel}
							activeOpacity={0.8}
						>
							<Text style={repoStyles.cancelBtnText}>Cancel</Text>
						</TouchableOpacity>
					</View>
				</View>
			</View>
		</Modal>
	);
};

const repoStyles = StyleSheet.create({
	overlay: {
		flex: 1,
		backgroundColor: 'rgba(0,0,0,0.82)',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 20,
	},
	backdropHit: {
		// Invisible full-screen tap target behind the card for tap-outside-to-close
		...StyleSheet.absoluteFillObject,
	},
	card: {
		backgroundColor: '#1c2233',
		borderRadius: 18,
		padding: 24,
		width: '100%',
		maxWidth: 520,
		zIndex: 1,
	},
	title: {
		color: '#fff',
		fontWeight: '700',
		fontSize: 18,
		textAlign: 'center',
		marginBottom: 4,
	},
	subtitle: {
		color: 'rgba(255,255,255,0.55)',
		fontSize: 13,
		textAlign: 'center',
		marginBottom: 16,
	},
	coords: {
		color: 'rgba(255,255,255,0.38)',
		fontSize: 11,
		textAlign: 'center',
		marginTop: 8,
		marginBottom: 4,
	},
	btnRow: {
		flexDirection: 'row',
		gap: 12,
		marginTop: 20,
	},
	btn: {
		flex: 1,
		paddingVertical: 13,
		borderRadius: 10,
		alignItems: 'center',
	},
	applyBtn: { backgroundColor: '#4c956c' },
	applyBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
	cancelBtn: { backgroundColor: 'rgba(255,255,255,0.1)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)' },
	cancelBtnText: { color: '#fff', fontWeight: '600', fontSize: 15 },
});

// ── Main Component ─────────────────────────────────────────────────────────────

const ImageGallery = ({ isEditMode, onImagesUpdated, initialImages, isPreview, isPreviewMode = false, maxImages = null, onLimitReached, showPlusBadge = false }) => {
	const previewImages = [
		require("../../assets/Popup Background.png"),
		{ uri: "https://source.unsplash.com/KAaEWMqFc8A" },
		{ uri: "https://source.unsplash.com/iggWDxHTAUQ" },
		{ uri: "https://source.unsplash.com/1VqHRwxcCCw" },
	];

	const [images, setImages] = useState(isPreview ? previewImages : initialImages || []);
	const [currentImage, setCurrentImage] = useState(0);
	const [isHovered, setIsHovered] = useState(false);
	const [repositionIdx, setRepositionIdx] = useState(null);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadError, setUploadError] = useState(null);
	const fileInputRef = useRef(null);

	const [fadeAnim] = useState(new Animated.Value(1));

	const handleAddImage = () => {
		if (maxImages !== null && images.length >= maxImages) {
			onLimitReached?.();
			return;
		}
		fileInputRef.current?.click();
	};

	const handleFileChange = async (event) => {
		const files = Array.from(event.target.files || []);
		if (!files.length) return;
		// Reset the input so the same file can be re-selected if needed
		event.target.value = '';

		if (maxImages !== null && images.length + files.length > maxImages) {
			onLimitReached?.();
			return;
		}

		if (isPreviewMode) {
			const previewUrls = typeof URL !== 'undefined'
				? files.map((f) => URL.createObjectURL(f)).filter(Boolean)
				: [];
			if (!previewUrls.length) return;
			const updated = [...images, ...previewUrls];
			setImages(updated);
			onImagesUpdated?.(updated);
			return;
		}

		setIsUploading(true);
		setUploadError(null);
		const storage = getStorage(app);
		try {
			const uploadedUrls = [];
			for (const file of files) {
				const storageRef = ref(storage, `eventPageImages/${Date.now()}-${file.name}`);
				await uploadBytes(storageRef, file);
				uploadedUrls.push(await getDownloadURL(storageRef));
			}
			const updated = [...images, ...uploadedUrls];
			setImages(updated);
			onImagesUpdated?.(updated);
			// Jump to the first newly uploaded photo
			setCurrentImage(updated.length - uploadedUrls.length);
		} catch (error) {
			console.error('Error uploading image:', error);
			setUploadError('Upload failed — please try again.');
		} finally {
			setIsUploading(false);
		}
	};

	const handleDeleteImage = async (image, index) => {
		const normalized = normalizeImage(image);
		if (!isPreviewMode && normalized.uri && !normalized.uri.startsWith('blob:')) {
			try {
				await deleteObject(ref(getStorage(app), normalized.uri));
			} catch {
				// External URL or already removed — keep UI responsive
			}
		}
		const updated = images.filter((_, i) => i !== index);
		setImages(updated);
		onImagesUpdated?.(updated);
		setCurrentImage(Math.min(currentImage, Math.max(0, updated.length - 1)));
	};

	// Fade helper: out → run callback → in
	const doFade = useCallback((cb) => {
		Animated.timing(fadeAnim, { toValue: 0, duration: 220, useNativeDriver: true }).start(() => {
			cb?.();
			Animated.timing(fadeAnim, { toValue: 1, duration: 220, useNativeDriver: true }).start();
		});
	}, [fadeAnim]);

	const goNext = useCallback(() => {
		if (images.length <= 1) return;
		doFade(() => setCurrentImage((prev) => (prev + 1) % images.length));
	}, [images.length, doFade]);

	const goPrev = useCallback(() => {
		if (images.length <= 1) return;
		doFade(() => setCurrentImage((prev) => (prev - 1 + images.length) % images.length));
	}, [images.length, doFade]);

	// Auto-slide: paused while in edit mode OR while the widget is hovered
	useEffect(() => {
		if (images.length <= 1 || isEditMode || isHovered) return;
		const interval = setInterval(() => {
			doFade(() => setCurrentImage((prev) => (prev + 1) % images.length));
		}, 4000);
		return () => clearInterval(interval);
	}, [images.length, isEditMode, isHovered, doFade]);

	useEffect(() => {
		setImages(isPreview ? previewImages : initialImages || []);
	}, [initialImages, isPreview]);

	useEffect(() => {
		if (images.length === 0) setCurrentImage(0);
		else if (currentImage >= images.length) setCurrentImage(images.length - 1);
	}, [images.length]);

	const handleRepositionSave = ({ position }) => {
		if (repositionIdx === null) return;
		const updated = images.map((img, i) => {
			if (i !== repositionIdx) return img;
			const n = normalizeImage(img);
			if (n._requireModule) return img; // can't reposition local asset
			return { uri: n.uri, position };
		});
		setImages(updated);
		onImagesUpdated?.(updated);
		setRepositionIdx(null);
	};

	const hasPhotos = images.length > 0;
	const normalizedCurrent = hasPhotos ? normalizeImage(images[currentImage]) : null;
	const currentPosition = normalizedCurrent?.position || '50% 50%';
	const isLocalAsset = !!normalizedCurrent?._requireModule;

	// Visitor view with no photos — friendly placeholder
	if (!hasPhotos && !isEditMode && !isPreview) {
		return (
			<View style={styles.comingSoonContainer}>
				<Text style={styles.comingSoonIcon}>📸</Text>
				<Text style={styles.comingSoonTitle}>Photos Coming Soon</Text>
				<Text style={styles.comingSoonSubtitle}>Check back later — this graduate is still adding their photos!</Text>
			</View>
		);
	}

	return (
		<>
			{repositionIdx !== null && (
				<RepositionModal
					image={images[repositionIdx]}
					onSave={handleRepositionSave}
					onCancel={() => setRepositionIdx(null)}
				/>
			)}

			<View
				style={isPreview ? styles.previewGalleryContainer : styles.galleryContainer}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				<input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} accept="image/*" multiple />

				{/* Image + overlaid controls */}
				<View style={styles.imageShellWrapper}>
					<View style={styles.imageShell}>
						{hasPhotos ? (
							isLocalAsset ? (
								<Animated.Image
									source={normalizedCurrent._requireModule}
									style={[isPreview ? styles.previewImageStyle : styles.imageStyle, { opacity: fadeAnim }]}
								/>
							) : (
								// Animated.View handles opacity fade; inner div carries background-image/position
								// because RN Web strips unknown CSS props from Animated.View's style system.
								<Animated.View
									style={[
										isPreview ? styles.previewImageStyle : styles.imageStyle,
									{ opacity: fadeAnim, overflow: 'hidden', borderRadius: isMobile ? 12 : 14 },
									]}
								>
									<div
										style={{
											width: '100%',
											height: '100%',
											backgroundImage: `url(${normalizedCurrent?.uri})`,
											backgroundSize: 'cover',
											backgroundPosition: currentPosition,
										}}
									/>
								</Animated.View>
							)
						) : (
							<View style={styles.emptyGalleryState}>
								<Text style={styles.emptyGalleryTitle}>No Photos Yet</Text>
								<Text style={styles.emptyGallerySubtitle}>Add a few images to create a slideshow.</Text>
							</View>
						)}

						{/* Dot indicators */}
						{hasPhotos && images.length > 1 && (
							<View style={styles.slideDots}>
								{images.map((_, index) => (
									<TouchableOpacity
										key={index}
										onPress={() => doFade(() => setCurrentImage(index))}
										style={[styles.slideDot, currentImage === index && styles.slideDotActive]}
									/>
								))}
							</View>
						)}

						{/* Edit-mode top bar: photo counter + reposition button */}
						{isEditMode && !isPreview && hasPhotos && (
							<View style={styles.editTopBar}>
								{images.length > 1 && (
									<View style={styles.photoCounter}>
										<Text style={styles.photoCounterText}>{currentImage + 1} / {images.length}</Text>
									</View>
								)}
								{!isLocalAsset && (
									<TouchableOpacity
										onPress={() => setRepositionIdx(currentImage)}
										style={styles.repositionBtn}
										activeOpacity={0.8}
									>
										<Text style={styles.repositionBtnText}>↕ Reposition</Text>
									</TouchableOpacity>
								)}
							</View>
						)}

					{/* Upload-in-progress overlay — covers the image while files are uploading */}
					{isUploading && (
						<View style={styles.uploadOverlay}>
							<View style={styles.uploadOverlayInner}>
								<Text style={styles.uploadOverlayText}>Uploading…</Text>
							</View>
						</View>
					)}
				</View>

				{/* Arrow navigation — outside imageShell to avoid overflow:hidden clipping */}
				{hasPhotos && images.length > 1 && !isPreview && (
						<>
							<TouchableOpacity style={[styles.arrowBtn, styles.arrowLeft]} onPress={goPrev} activeOpacity={0.75}>
								<Text style={styles.arrowText}>‹</Text>
							</TouchableOpacity>
							<TouchableOpacity style={[styles.arrowBtn, styles.arrowRight]} onPress={goNext} activeOpacity={0.75}>
								<Text style={styles.arrowText}>›</Text>
							</TouchableOpacity>
						</>
					)}
				</View>

				{/* Upload error banner */}
				{uploadError && (
					<View style={styles.uploadErrorBanner}>
						<Text style={styles.uploadErrorText}>{uploadError}</Text>
						<TouchableOpacity onPress={() => setUploadError(null)} style={{ marginLeft: 8 }}>
							<Text style={styles.uploadErrorDismiss}>✕</Text>
						</TouchableOpacity>
					</View>
				)}

				{/* Owner toolbar */}
				{isEditMode && !isPreview && (
					<View style={styles.ownerToolbar}>
						<TouchableOpacity onPress={handleAddImage} style={[styles.toolbarButton, styles.addButton]}>
							<Text style={styles.addButtonText}>Add Photos {showPlusBadge ? "+" : ""}</Text>
						</TouchableOpacity>
						{hasPhotos && (
							<TouchableOpacity
								onPress={() => handleDeleteImage(images[currentImage], currentImage)}
								style={[styles.toolbarButton, styles.deleteImageButton]}
							>
								<Text style={styles.deleteButton}>✕ Remove This Photo</Text>
							</TouchableOpacity>
						)}
					</View>
				)}
			</View>
		</>
	);
};

const styles = StyleSheet.create({
	previewGalleryContainer: {
		borderRadius: 12,
		padding: 4,
		width: isMobile ? "90vw" : 120,
		height: isMobile ? "90vw" : 120,
	},
	previewImageStyle: {
		width: "100%",
		height: "100%",
		borderRadius: 10,
	},

	galleryContainer: {
		width: '100%',
		maxWidth: isMobile ? '100%' : 650,
		alignSelf: "center",
		borderRadius: isMobile ? 20 : 18,
		backgroundColor: "rgba(254, 253, 226, 0.96)",
		borderWidth: 1,
		borderColor: "rgba(76, 149, 108, 0.18)",
		padding: isMobile ? 8 : 10,
		shadowColor: "rgba(0, 0, 0, 0.25)",
		shadowOffset: { width: 0, height: 6 },
		shadowOpacity: 0.18,
		shadowRadius: 12,
		elevation: 5,
	},
	// Wrapper that holds imageShell + absolutely-positioned arrows (arrows sit outside
	// imageShell so they aren't clipped by overflow:hidden)
	imageShellWrapper: {
		position: 'relative',
		width: '100%',
	},
	imageShell: {
		position: "relative",
		width: "100%",
		aspectRatio: 16 / 10,
		borderRadius: isMobile ? 12 : 14,
		overflow: "hidden",
		backgroundColor: "#f8fafc",
	},
	imageStyle: {
		width: "100%",
		height: "100%",
		borderRadius: isMobile ? 12 : 14,
	},
	// ── Arrow navigation ────────────────────────────────────────────────────────
	arrowBtn: {
		position: 'absolute',
		top: 0,
		bottom: 0,
		width: isMobile ? 44 : 52,
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 20,
	},
	arrowLeft: {
		left: 0,
		borderTopLeftRadius: 14,
		borderBottomLeftRadius: 14,
		// subtle gradient-like fade on the left edge
		backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.25), transparent)',
	},
	arrowRight: {
		right: 0,
		borderTopRightRadius: 14,
		borderBottomRightRadius: 14,
		backgroundImage: 'linear-gradient(to left, rgba(0,0,0,0.25), transparent)',
	},
	arrowText: {
		color: '#fff',
		fontSize: isMobile ? 34 : 42,
		fontWeight: '300',
		lineHeight: isMobile ? 34 : 42,
		textShadowColor: 'rgba(0,0,0,0.6)',
		textShadowOffset: { width: 0, height: 1 },
		textShadowRadius: 4,
		marginTop: -4, // visual optical centering of the glyph
	},
	// ── Edit-mode overlays ──────────────────────────────────────────────────────
	editTopBar: {
		position: 'absolute',
		top: 10,
		right: 10,
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		zIndex: 10,
	},
	photoCounter: {
		backgroundColor: 'rgba(0,0,0,0.52)',
		borderRadius: 20,
		paddingVertical: 4,
		paddingHorizontal: 10,
	},
	photoCounterText: {
		color: '#fff',
		fontSize: 12,
		fontWeight: '600',
	},
	repositionBtn: {
		backgroundColor: 'rgba(76, 149, 108, 0.9)',
		borderRadius: 20,
		paddingVertical: 5,
		paddingHorizontal: 13,
	},
	repositionBtnText: {
		color: '#fff',
		fontSize: 12,
		fontWeight: '700',
	},
	// ── Upload feedback ────────────────────────────────────────────────────────
	uploadOverlay: {
		position: 'absolute',
		inset: 0,
		zIndex: 30,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'rgba(0,0,0,0.45)',
		borderRadius: isMobile ? 12 : 14,
	},
	uploadOverlayInner: {
		backgroundColor: 'rgba(28, 34, 51, 0.92)',
		borderRadius: 12,
		paddingVertical: 12,
		paddingHorizontal: 22,
	},
	uploadOverlayText: {
		color: '#fff',
		fontSize: 14,
		fontWeight: '700',
		textAlign: 'center',
	},
	uploadErrorBanner: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		backgroundColor: '#fee2e2',
		borderColor: '#fecaca',
		borderWidth: 1,
		borderRadius: 10,
		paddingVertical: 8,
		paddingHorizontal: 12,
		marginBottom: 8,
	},
	uploadErrorText: {
		color: '#dc2626',
		fontSize: 13,
		fontWeight: '600',
		flex: 1,
	},
	uploadErrorDismiss: {
		color: '#dc2626',
		fontSize: 14,
		fontWeight: '700',
	},
	// ── Toolbar ─────────────────────────────────────────────────────────────────
	ownerToolbar: {
		flexDirection: isMobile ? "column" : "row",
		alignItems: "center",
		justifyContent: "center",
		gap: 10,
		paddingTop: 10,
	},
	toolbarButton: {
		paddingVertical: 9,
		paddingHorizontal: 14,
		borderRadius: 10,
		minWidth: isMobile ? "100%" : 140,
		alignItems: "center",
	},
	addButton: {
		backgroundColor: '#4C956C',
	},
	addButtonText: {
		color: "white",
		fontWeight: "bold",
		textAlign: "center",
		fontSize: isMobile ? 14 : 15,
	},
	deleteImageButton: {
		backgroundColor: "#fee2e2",
		borderWidth: 1,
		borderColor: "#fecaca",
	},
	deleteButton: {
		color: "#dc2626",
		fontWeight: "bold",
		fontSize: isMobile ? 13 : 14,
	},
	// ── Dot indicators ──────────────────────────────────────────────────────────
	slideDots: {
		position: "absolute",
		left: 0,
		right: 0,
		bottom: 10,
		flexDirection: "row",
		justifyContent: "center",
		gap: 6,
	},
	slideDot: {
		width: 8,
		height: 8,
		borderRadius: 4,
		backgroundColor: "rgba(255,255,255,0.55)",
	},
	slideDotActive: {
		width: 22,
		backgroundColor: "#ffffff",
	},
	// ── Empty / placeholder states ───────────────────────────────────────────────
	emptyGalleryState: {
		flex: 1,
		alignItems: "center",
		justifyContent: "center",
		paddingHorizontal: 18,
		borderWidth: 1,
		borderColor: "rgba(76, 149, 108, 0.18)",
		borderStyle: "dashed",
		borderRadius: isMobile ? 12 : 14,
	},
	emptyGalleryTitle: {
		fontSize: 17,
		fontWeight: "700",
		color: "#2d5a3f",
		marginBottom: 6,
		textAlign: "center",
	},
	emptyGallerySubtitle: {
		fontSize: 13,
		color: "#64748b",
		textAlign: "center",
	},
	comingSoonContainer: {
		borderRadius: 20,
		borderWidth: 2,
		borderColor: '#e2e8f0',
		borderStyle: 'dashed',
		backgroundColor: '#f8fafc',
		paddingVertical: 40,
		paddingHorizontal: 24,
		alignItems: 'center',
		justifyContent: 'center',
		marginVertical: 8,
	},
	comingSoonIcon: { fontSize: 36, marginBottom: 12 },
	comingSoonTitle: { fontSize: 17, fontWeight: '700', color: '#475569', marginBottom: 6, textAlign: 'center' },
	comingSoonSubtitle: { fontSize: 13, color: '#94a3b8', textAlign: 'center', lineHeight: 20 },
});

export default ImageGallery;
